import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  PositionedThreadSchema,
  type ThreadPreviewPositioningResponse,
  ThreadPreviewPositioningResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'

import type {LegacyThreadPreviewPositioning} from './use-thread-preview-positioning'

/**
 * TEMPORARY — proto migration shim for the `thread_preview_positioning` page-data endpoint.
 *
 * The generated proto `ThreadPreviewPositioningResponse` is the canonical shape the app consumes.
 * When the backend `thread_preview_positioning_proto` flag is ON the endpoint returns proto3-JSON,
 * which `queryFnFetch` decodes into the proto message via the generated schema. When the flag is OFF
 * the endpoint still renders the legacy JSON object keyed by review-thread id, which we convert UP
 * into the proto shape here so consumers only ever see the proto type.
 *
 * Once the flag is fully rolled out, delete this whole file (and the `LegacyThreadPreviewPositioning`
 * type) — consumers already read the proto shape and need no further changes.
 */
export function legacyToProtoThreadPreviewPositioning(
  legacy: LegacyThreadPreviewPositioning,
): ThreadPreviewPositioningResponse {
  const threads: ThreadPreviewPositioningResponse['threads'] = {}

  for (const [threadId, thread] of Object.entries(legacy)) {
    threads[threadId] = create(PositionedThreadSchema, {
      diffLines: thread.diffLines.map(line => ({
        html: line.html,
        // Proto3 omits absent Int32Value fields; legacy emits explicit null.
        left: line.left ?? undefined,
        right: line.right ?? undefined,
        text: line.text,
        type: line.type,
      })),
      endDiffSide: thread.endDiffSide,
      endLine: thread.endLine ?? undefined,
      path: thread.path,
      startDiffSide: thread.startDiffSide,
      startLine: thread.startLine ?? undefined,
      isOutdated: thread.isOutdated ?? false,
      originalDiffPathUri: thread.originalDiffPathUri ?? undefined,
    })
  }

  return create(ThreadPreviewPositioningResponseSchema, {threads})
}

/**
 * Normalizes a `thread_preview_positioning` response into the canonical proto
 * `ThreadPreviewPositioningResponse`. `queryFnFetch` already decodes proto-mode (flag-on) responses
 * into a proto message via the generated schema (so they carry `$typeName`); only the legacy
 * (flag-off) JSON still needs converting up to the proto shape.
 */
export function normalizeThreadPreviewPositioning(
  result: ThreadPreviewPositioningResponse | LegacyThreadPreviewPositioning,
): ThreadPreviewPositioningResponse {
  return '$typeName' in result ? result : legacyToProtoThreadPreviewPositioning(result)
}
